import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type User,
} from 'firebase/auth';

// Thin wrapper around Firebase GCIP TOTP MFA. The platform uses Identity
// Platform (paid Auth tier) which supports TOTP and SMS factors — we only
// expose TOTP because:
//   1. Free for the user (no SMS provider cost)
//   2. Phishing-resistant — TOTP secrets never leave the user's device
//   3. SMS adds carrier dependency we don't want at this scale
//
// TOTP enrollment is a 3-step dance: get a session, generate a secret +
// QR-code URI, then assert with a user-typed code. The user can scan the
// QR with any authenticator app (Authy, 1Password, Google Authenticator).
//
// PRECONDITION: MFA must be enabled in the GCP Identity Platform console.
// Without that, enroll() throws auth/unsupported-tenant-operation. The
// platform admin enables it once per environment via:
//   GCP Console → Identity Platform → Multi-factor authentication →
//   Enable TOTP.

export interface TotpEnrollmentSecret {
  /** The raw shared secret (base32). Show as fallback for users whose
   *  authenticator can't scan QR codes. */
  secretKey: string;
  /** otpauth:// URL — encode as a QR code in the UI. */
  otpauthUrl: string;
  /** Opaque session handle that completeTotpEnrollment needs. */
  secret: TotpSecret;
}

/** Read-only view of currently enrolled factors. Use to render the manage
 *  list + decide whether to show the "enroll" or "unenroll" affordance. */
export function getEnrolledFactors(user: User): MultiFactorInfo[] {
  return multiFactor(user).enrolledFactors;
}

/** Step 1 of enrollment. Throws if the tenant doesn't have MFA enabled or
 *  if the user's session is too old (Firebase requires a recent sign-in
 *  for security-sensitive operations — re-prompt for password if so). */
export async function startTotpEnrollment(user: User): Promise<TotpEnrollmentSecret> {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  // The display label here is just what shows in the user's authenticator
  // app — accountName is the email so they know which account it belongs
  // to; the issuer is the product name.
  const otpauthUrl = secret.generateQrCodeUrl(
    user.email ?? 'account',
    'X-One Cyber Aware',
  );
  return { secretKey: secret.secretKey, otpauthUrl, secret };
}

/** Step 2 of enrollment. The user opens their authenticator, scans the QR
 *  code, then types the 6-digit code that appears. We assert that code
 *  against the secret we generated in step 1; success enrolls the factor.
 *  `displayName` shows up in the user's enrolled-factors list (e.g.,
 *  "iPhone Authenticator"). */
export async function completeTotpEnrollment(
  user: User,
  enrollment: TotpEnrollmentSecret,
  oneTimeCode: string,
  displayName: string,
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    enrollment.secret,
    oneTimeCode.trim(),
  );
  await multiFactor(user).enroll(assertion, displayName.trim() || 'Authenticator app');
}

/** Remove an enrolled factor by uid. The uid lives on
 *  MultiFactorInfo.uid — see getEnrolledFactors. */
export async function unenrollFactor(user: User, factorUid: string): Promise<void> {
  await multiFactor(user).unenroll(factorUid);
}

/** Sign-in side: when a password sign-in returns auth/multi-factor-auth-
 *  required, the caller catches the error, calls this to get the resolver
 *  + the list of factors the user can challenge with, then prompts the
 *  user to type a TOTP code from one of them. */
export async function resolveMfaChallenge(
  resolver: MultiFactorResolver,
  factorUid: string,
  oneTimeCode: string,
): Promise<User> {
  // Build a TOTP assertion for the chosen hint and resolve.
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    factorUid,
    oneTimeCode.trim(),
  );
  const cred = await resolver.resolveSignIn(assertion);
  return cred.user;
}
